/**
 * Derives when the single-order "Mark as completed" action must stay disabled
 * because a J-code's persisted CPT-table values (start/end date, auth #, total
 * visits) have drifted from the auth-letter values shown on the Authorization
 * Details screen. The user must reconcile the two before completing the order.
 *
 * @module authLetterFieldMatchMarkCompleteGuard
 */
import type { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { getAuthLetterMismatchesForJCode } from "../../worklists/singleOrder/cptCodes/utils/authLetterFieldComparison";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

/**
 * Returns true when any J-code carrying an auth letter has at least one field
 * that differs between its CPT-table values and the auth-letter values.
 *
 * @input authOnFile - Auth-on-file subtree from order detail (`entries` / `auth_entries`)
 * @return boolean: Whether the mark-as-completed primary action should be disabled
 */
export const shouldDisableMarkCompleteForAuthLetterFieldMismatch = (
  authOnFile: AuthOnFile | null | undefined,
): boolean => {
  const jCodes = getAllJCodesFromAuthOnFile(authOnFile ?? undefined);
  if (!jCodes?.length) {
    return false;
  }
  return jCodes.some(
    (jCode) => getAuthLetterMismatchesForJCode(jCode).length > 0,
  );
};
